import { useState, useRef, useEffect } from "react";
import { animate, stagger } from "animejs";
import useScrollReveal from "../useScrollReveal";
import useSectionAnalytics, { mergeRefs } from "../useSectionAnalytics";
import { useTranslation } from "react-i18next";
import { trackContactSubmit } from "../utils/analytics";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
};

export default function Contact() {
  const [ref, visible] = useScrollReveal();
  const analyticsRef = useSectionAnalytics("contact");
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // null | "success" | "error"

  // Stagger the form fields in one after another instead of the whole
  // card fading in as a single flat block.
  useEffect(() => {
    if (!visible || hasAnimatedRef.current || !cardRef.current) return;
    hasAnimatedRef.current = true;
    const fields = cardRef.current.querySelectorAll(".contact-field");
    animate(fields, {
      opacity: [0, 1],
      translateY: [16, 0],
      duration: 500,
      delay: stagger(90),
      ease: "outQuad",
      onComplete: (a) =>
        a.targets.forEach((el) => {
          el.style.opacity = "";
          el.style.transform = "";
        }),
    });
  }, [visible]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const submit = async () => {
    if (loading) return;

    setLoading(true);
    setStatus(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error("Submit failed");
      }

      trackContactSubmit();
      setForm(emptyForm);
      setStatus("success");
      setTimeout(() => setStatus(null), 3500);
    } catch (error) {
      console.error("Contact submit error:", error);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={mergeRefs(ref, analyticsRef)}
      className={`contact-section ${visible ? "visible" : ""}`}
    >

      <h2>
        {t("contact.title")}
      </h2>

      <p className="contact-text">
        {t("contact.desc")}
      </p>

      <p className="contact-email">
        {t("contact.emailLabel")}{" "}
        <span>
          support@kitmeechai.com
        </span>
      </p>

      <p className="contact-email">
        {t("contact.phoneLabel")}{" "}
        <span>
          {t("contact.phone")}
        </span>
      </p>

      <p className="contact-or">
        {t("contact.or")}
      </p>

      <div className="contact-card-wrap">
        <div className="contact-card-glow" />

        <div className="contact-card" ref={cardRef}>
          {status === "success" ? (
            <div className="contact-success">
              <svg className="contact-success__icon" viewBox="0 0 52 52">
                <circle className="contact-success__circle" cx="26" cy="26" r="24" fill="none" />
                <path className="contact-success__check" fill="none" d="M14 27l7 7 16-16" />
              </svg>
              <p className="contact-success__text">{t("contact.submitSuccess")}</p>
            </div>
          ) : (
            <>
              <div className="form-row contact-field">
                <input
                  required
                  name="firstName"
                  value={form.firstName}
                  placeholder={t("contact.firstName")}
                  onChange={handleChange}
                />
                <input
                  required
                  name="lastName"
                  value={form.lastName}
                  placeholder={t("contact.lastName")}
                  onChange={handleChange}
                />
              </div>

              <input
                required
                type="email"
                name="email"
                value={form.email}
                placeholder={t("contact.email")}
                onChange={handleChange}
                className="contact-field"
              />

              <input
                type="tel"
                name="phone"
                value={form.phone}
                placeholder={t("contact.phoneInput")}
                onChange={handleChange}
                className="contact-field"
              />

              <textarea
                required
                name="message"
                value={form.message}
                placeholder={t("contact.message")}
                rows="5"
                onChange={handleChange}
                className="contact-field"
              />

              {status === "error" && (
                <p className="contact-field contact-error">{t("contact.submitFailed")}</p>
              )}

              <button
                onClick={submit}
                disabled={loading}
                className="contact-field"
              >
                {loading
                  ? t("contact.sending")
                  : t("contact.submit")
                }
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
